import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatUnits } from 'ethers';
import { useWallet } from '../context/WalletContext';
import { useContracts } from '../hooks/useContracts';
import { useNotify } from '../context/NotificationContext';
import { useUserData } from '../hooks/useUserData';
import { usePlanConfig } from '../hooks/usePlanConfig';
import { fmtUSD, fmtGRX, parseUSD, tsToDate, bpsToPct, secondsToDHMS, roundWei } from '../utils/format';
import { parseError } from '../utils/errors';
import { PLAN_META } from '../utils/constants';
import { triggerSwapHighlight } from '../hooks/useSwapHighlight';
import { useCutoffInfo } from '../hooks/useCutoffInfo';
import { computeInvPending } from '../utils/roi';
import { planLockSeconds, pinksaleLockUrl } from '../utils/pinksale';
import { ACTIVE_NETWORK } from '../config/network';
import { waitForReceiptWithRetry, receiptSucceeded } from '../utils/txWait';
import GlassCard from '../components/common/GlassCard';
import NeonButton from '../components/common/NeonButton';
import ConnectPrompt from '../components/common/ConnectPrompt';
import HexLoader from '../components/loader/HexLoader';
import RingProgress from '../components/common/RingProgress';
import AtomSpinner from '../components/loader/AtomSpinner';
import Icon from '../components/common/Icon';
import CutoffTimer from '../components/common/CutoffTimer';

const Invest = () => {
  const nav = useNavigate();
  const { account } = useWallet();
  const notify = useNotify();
  const c = useContracts();
  const { userId, grxBalance, usdtBalance, investments, loading } = useUserData();
  const { config } = usePlanConfig();

  const [tab, setTab] = useState('invest');
  const [busy, setBusy] = useState(false);
  const [txLabel, setTxLabel] = useState('');

  if (!account) return <ConnectPrompt />;

  const invCount = investments ? investments.length : 0;

  return (
    <div className="animate-fade-up">
      <div className="page-title">Investments</div>
      <div className="page-subtitle">Open new positions or manage your active portfolio.</div>

      <div className="invest-tabs">
        <button className={'it-btn' + (tab === 'invest' ? ' active' : '')} onClick={() => setTab('invest')}>
          <span>⚡</span><span>Invest Now</span>
        </button>
        <button className={'it-btn' + (tab === 'my' ? ' active' : '')} onClick={() => setTab('my')}>
          <span>⬢</span><span>My Investments</span>
          <span className="it-count">{invCount}</span>
        </button>
      </div>

      {tab === 'invest' && (
        <InvestNowTab
          c={c} nav={nav} notify={notify} config={config}
          account={account} userId={userId}
          grxBalance={grxBalance} usdtBalance={usdtBalance}
          busy={busy} setBusy={setBusy} setTxLabel={setTxLabel}
        />
      )}

      {tab === 'my' && (
        <MyInvestmentsTab
          c={c} notify={notify} userId={userId}
          investments={investments} loading={loading}
          busy={busy} setBusy={setBusy} setTxLabel={setTxLabel}
        />
      )}

      {busy && <HexLoader label={txLabel || 'Processing'} sub="Please confirm the transaction(s) in your wallet." />}
    </div>
  );
};

/* ═══════════════════════ INVEST NOW ═══════════════════════ */
const InvestNowTab = ({ c, nav, notify, config, account, userId, grxBalance, usdtBalance, busy, setBusy, setTxLabel }) => {
  const [planType, setPlanType] = useState(0);
  const [usdtAmount, setUsdtAmount] = useState('');

  const price = config?.grxPriceUSDT ? BigInt(config.grxPriceUSDT) : 0n;

  const usdEquiv = useMemo(() => {
    if (!usdtAmount) return 0n;
    try { return parseUSD(usdtAmount); } catch { return 0n; }
  }, [usdtAmount]);

  // For 7yr Contributor the user's total input is split 70/30:
  //   70% → GRX (locked in PinkSale + becomes the investment record + earns ROI)
  //   30% → USDT (forwarded 50/50 to LP + admin, drives the 10X contributor payout)
  // For all other plans the full amount goes to GRX.
  const investUsd = planType === 3 ? (usdEquiv * 7000n) / 10000n : usdEquiv;
  const usdt30   = planType === 3 ? (usdEquiv * 3000n) / 10000n : 0n;

  const grxNeeded = useMemo(() => {
    if (investUsd <= 0n || price === 0n) return 0n;
    try {
      const raw = (investUsd * 10n ** 18n) / price;
      // Round to 4 decimals → identical value between lock() and invest() calls.
      return roundWei(raw, 4);
    } catch { return 0n; }
  }, [investUsd, price]);

  if (!userId || userId === 0n) {
    return (
      <div className="alert a-warn">
        Please <a href="/#/register" onClick={(e) => { e.preventDefault(); nav('/register'); }}>register</a> first before investing.
      </div>
    );
  }

  const isLocked = planType !== 0;

  const setMaxUsdt = () => {
    try {
      if (price === 0n) return;
      const usdFromGrx = parseFloat(formatUnits(((BigInt(grxBalance) * price) / (10n ** 18n)).toString(), 18));
      setUsdtAmount(usdFromGrx.toFixed(2));
    } catch {}
  };

  const doInvest = async () => {
    try {
      if (grxNeeded <= 0n) throw new Error('Enter an amount');
      if (price === 0n) throw new Error('Price feed unavailable');
      if (BigInt(grxBalance) < grxNeeded) throw new Error('Insufficient GRX balance.');

      setBusy(true);
      const usdtParam = planType === 3 ? usdt30 : 0n;
      const storageAddr = c.addresses.storage;

      // GRX approval — always to Storage now (contract creates the PinkSale
      // lock itself inside invest(), atomically. No orphan locks possible.)
      setTxLabel('Approving GRX');
      const currentGrxAllow = await c.read.grx.allowance(account, storageAddr);
      if (BigInt(currentGrxAllow) < grxNeeded) {
        const aTx = await c.write.grx.approve(storageAddr, grxNeeded);
        await aTx.wait();
        notify.success('Approved', 'GRX approved.');
      }

      // 7yr USDT approval (contributor 30% side)
      if (planType === 3 && usdt30 > 0n) {
        if (BigInt(usdtBalance) < usdt30) throw new Error('Insufficient USDT for contributor 30% side.');
        const usdtAllow = await c.read.usdt.allowance(account, storageAddr);
        if (BigInt(usdtAllow) < usdt30) {
          setTxLabel('Approving USDT');
          const aTx = await c.write.usdt.approve(storageAddr, usdt30);
          await aTx.wait();
          notify.success('Approved', 'USDT approved.');
        }
      }

      const invCountBefore = BigInt(
        await c.read.store.getInvestmentCount(userId).catch(() => 0n)
      );

      setTxLabel('Submitting Investment');
      // Estimate gas from the chain + add a 30% safety buffer. Mobile wallets
      // (SafePal etc.) will still let the user tweak the final gas limit on
      // the confirmation screen. Fall back to a large hardcoded value only
      // if estimateGas itself errors (network hiccup, not a revert).
      let gasLimit;
      try {
        const est = await c.write.logic.invest.estimateGas(planType, grxNeeded, usdtParam);
        gasLimit = (BigInt(est) * 130n) / 100n;
      } catch {
        gasLimit = 3_000_000n;
      }
      const tx = await c.write.logic.invest(
        planType, grxNeeded, usdtParam,
        { gasLimit }
      );

      setTxLabel('Confirming Transaction');
      const signerProvider = c.write.logic.runner && c.write.logic.runner.provider;
      const readProvider   = c.read.logic.runner;
      const receipt = await waitForReceiptWithRetry(tx, [signerProvider, readProvider]);

      if (receipt) {
        if (!receiptSucceeded(receipt)) {
          throw new Error('Investment tx reverted on-chain.');
        }
      } else {
        setTxLabel('Verifying On-Chain');
        await new Promise((r) => setTimeout(r, 3000));
        const invCountAfter = BigInt(
          await c.read.store.getInvestmentCount(userId).catch(() => invCountBefore)
        );
        if (invCountAfter <= invCountBefore) {
          throw new Error(
            'Could not confirm the investment within the timeout. Check the transaction on BscScan: ' + tx.hash
          );
        }
      }

      notify.success('Invested', `${PLAN_META[planType].name} activated.`);
      setUsdtAmount('');
    } catch (e) {
      notify.error('Invest Failed', parseError(e));
    } finally {
      setBusy(false); setTxLabel('');
    }
  };

  const lockDur = planLockSeconds(planType);

  return (
    <>
      <GlassCard title="Select Plan">
        <div className="plan-grid">
          {PLAN_META.map((p) => (
            <div
              key={p.id}
              className={`plan-card ${planType === p.id ? 'selected' : ''}`}
              onClick={() => setPlanType(p.id)}
            >
              <div className="plan-name">{p.name}</div>
              <div className="plan-tag">{p.tag}</div>
              <div className="plan-apr">{(p.dailyBps / 100).toFixed(2)}%</div>
              <div className="plan-apr-sub">daily · Lock {p.lockYears || 0}y</div>
            </div>
          ))}
        </div>
      </GlassCard>

      <div className="mt-3" />

      <GlassCard title="Investment Amount">
        <div className="input-label">
          <span>Amount in USDT</span>
          <span className="wallet-bal-chips">
            <span className="wbc grx">GRX: <strong>{fmtGRX(grxBalance)}</strong></span>
            {planType === 3 && (
              <span className="wbc usdt">USDT: <strong>{fmtUSD(usdtBalance)}</strong></span>
            )}
          </span>
        </div>
        <div className="amount-input-wrap">
          <input type="number" placeholder="0.00" value={usdtAmount} onChange={(e) => setUsdtAmount(e.target.value)} />
          <button className="max-btn" onClick={setMaxUsdt} type="button">MAX</button>
        </div>
        <div className="invest-summary mt-3">
          <div className="data-row"><span className="label">GRX Required</span><span className="value cyan">{fmtGRX(grxNeeded)}</span></div>
          {planType === 3 && (
            <div className="data-row"><span className="label">USDT Required</span><span className="value cyan">{fmtUSD(usdt30)}</span></div>
          )}
          {isLocked && (
            <div className="data-row">
              <span className="label">PinkSale Lock</span>
              <span className="value">{(lockDur / (365 * 24 * 3600)).toFixed(0)} years</span>
            </div>
          )}
        </div>

        <SwapCta
          grxNeeded={grxNeeded}
          grxBalance={BigInt(grxBalance || 0n)}
          usdt30={planType === 3 ? usdt30 : 0n}
          usdtBalance={BigInt(usdtBalance || 0n)}
          nav={nav}
        />

        <div className="mt-3">
          <NeonButton block size="lg" onClick={doInvest} loading={busy} disabled={!usdtAmount || grxNeeded === 0n}>
            {isLocked ? 'Lock GRX & Invest' : 'Approve & Invest'}
          </NeonButton>
          <div className="mono text-center mt-2" style={{ fontSize: 11, color: 'var(--grx-text-mute)' }}>
            {isLocked
              ? 'This will create a PinkSale lock owned by you, then register the investment.'
              : `One tap handles GRX${planType === 3 ? ' + USDT' : ''} approval automatically.`}
          </div>
        </div>
      </GlassCard>
    </>
  );
};

/* ═══════════════════════ SWAP CTA ═══════════════════════ */
const SwapCta = ({ grxNeeded, grxBalance, usdt30, usdtBalance, nav }) => {
  if (grxNeeded <= 0n) return null;
  const grxShort  = grxNeeded > grxBalance   ? grxNeeded - grxBalance   : 0n;
  const usdtShort = usdt30    > usdtBalance  ? usdt30    - usdtBalance  : 0n;
  if (grxShort === 0n && usdtShort === 0n) return null;

  const go = () => { triggerSwapHighlight(); setTimeout(() => nav('/swap'), 250); };

  return (
    <div className="swap-cta">
      <div className="swap-cta-head">
        <div className="sc-icon"><Icon name="swap" size={20} /></div>
        <div className="swap-cta-title">Insufficient Balance</div>
      </div>
      <div className="swap-cta-body">
        {grxShort > 0n && (
          <div>You need <span className="short-amt">{fmtGRX(grxShort)}</span> more GRX for this investment.</div>
        )}
        {usdtShort > 0n && (
          <div style={{ marginTop: grxShort > 0n ? 6 : 0 }}>
            You also need <span className="short-amt">{fmtUSD(usdtShort)}</span> more USDT for the 30% side.
          </div>
        )}
      </div>
      <div className="swap-cta-actions">
        <NeonButton onClick={go}>
          <Icon name="swap" size={16} style={{ marginRight: 6 }} />
          Go to Swap
        </NeonButton>
      </div>
    </div>
  );
};

/* ═══════════════════════ MY INVESTMENTS ═══════════════════════ */
const MyInvestmentsTab = ({ c, notify, userId, investments, loading, busy, setBusy, setTxLabel }) => {
  const { currentCutoffIndex } = useCutoffInfo();

  const withdraw = async (idx) => {
    try {
      if (!c.write.rewards) throw new Error('Rewards contract not configured.');
      setBusy(true); setTxLabel('Withdrawing Principal');
      const tx = await c.write.rewards.withdrawFlexible(idx);
      await tx.wait();
      notify.success('Withdrawn', 'Flexible principal returned to wallet.');
    } catch (e) { notify.error('Withdraw Failed', parseError(e)); }
    finally { setBusy(false); setTxLabel(''); }
  };

  const accrue = async () => {
    try {
      setBusy(true); setTxLabel('Syncing ROI');
      const tx = await c.write.logic.accrueUser(userId);
      await tx.wait();
      notify.success('Synced', 'ROI updated on-chain.');
    } catch (e) { notify.error('Sync Failed', parseError(e)); }
    finally { setBusy(false); setTxLabel(''); }
  };

  if (loading) return <div className="empty-state"><AtomSpinner size="lg" /><div className="empty-title mt-3">Loading</div></div>;

  if (!investments || investments.length === 0) {
    return (
      <GlassCard>
        <div className="empty-state">
          <div className="empty-icon">⬢</div>
          <div className="empty-title">No investments yet</div>
          <div className="mt-2">Switch to the <strong>Invest Now</strong> tab to open your first position.</div>
        </div>
      </GlassCard>
    );
  }

  const explorerBase = 'https://testnet.bscscan.com';

  return (
    <>
      <div className="grid grid-2 mb-4">
        <CutoffTimer />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <NeonButton variant="ghost" size="sm" onClick={accrue} loading={busy}>Sync All ROI</NeonButton>
        </div>
      </div>

      <div className="wow-inv-grid">
        {investments.map((inv) => {
          const idx = Number(inv.index);
          const cap = BigInt(inv.bucketCapUSDT);
          const earned = BigInt(inv.bucketEarnedUSDT);
          const pending = computeInvPending(inv, currentCutoffIndex);
          const combined = earned + pending;
          const pct = cap > 0n ? Number((combined * 10000n) / cap) / 100 : 0;
          const meta = PLAN_META[Number(inv.planType)] || { name: 'Plan ' + inv.planType };
          const invType = Number(inv.investmentType);
          const now = Math.floor(Date.now() / 1000);
          const lockEnd = Number(inv.lockExpiresAt || 0);
          const timeLeft = lockEnd > now ? lockEnd - now : 0;
          const hasLock = BigInt(inv.pinkSaleLockId || 0n) > 0n;

          return (
            <div key={idx} className="wow-inv">
              <div className="wow-inv-glow" />
              <div className="wow-inv-head">
                <div className="wow-inv-icon">#{idx}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="wow-inv-title">{meta.name}</div>
                  <div className="wow-inv-sub">Opened {tsToDate(inv.timestamp)}</div>
                </div>
              </div>

              <div className="wow-badges">
                {inv.active ? <span className="badge">Active</span> : <span className="badge b-dim">Closed</span>}
                {inv.principalWithdrawn && <span className="badge b-warn">Withdrawn</span>}
                {hasLock && (
                  <a
                    className="badge b-cyan"
                    style={{ textDecoration: 'none' }}
                    href={`${explorerBase}/address/${c.addresses.pinkSaleLocker}#readContract`}
                    target="_blank" rel="noreferrer"
                    title="View PinkSale locker on BscScan"
                  >
                    🔒 Lock #{String(inv.pinkSaleLockId)}
                  </a>
                )}
                {invType === 1 && <span className="badge b-cyan">GIFT</span>}
                {invType === 2 && <span className="badge b-warn">LEADER</span>}
                <span className="badge">{bpsToPct(inv.dailyROIBps)} / day</span>
              </div>

              <div className="ring-center-wrap">
                <RingProgress pct={pct} size={140} stroke={12} label="Ceiling" />
              </div>

              <div className="wow-stats">
                <div className="wow-stat">
                  <div className="ws-label">Invested</div>
                  <div className="ws-value">{fmtUSD(inv.amountUSDT)}</div>
                </div>
                <div className="wow-stat cyan">
                  <div className="ws-label">GRX {Number(inv.planType) === 0 ? 'in Contract' : 'in PinkSale'}</div>
                  <div className="ws-value cyan">{fmtGRX(inv.amountGRX)}</div>
                </div>
                <div className="wow-stat">
                  <div className="ws-label">Earned</div>
                  <div className="ws-value neon">{fmtUSD(combined)}</div>
                </div>
                <div className="wow-stat cyan">
                  <div className="ws-label">Available</div>
                  <div className="ws-value cyan">{fmtUSD(pending)}</div>
                </div>
                <div className="wow-stat">
                  <div className="ws-label">Cap</div>
                  <div className="ws-value">{fmtUSD(cap)}</div>
                </div>
                <div className="wow-stat">
                  <div className="ws-label">Remaining</div>
                  <div className="ws-value">{fmtUSD(cap > combined ? cap - combined : 0n)}</div>
                </div>
              </div>

              {lockEnd > 0 && (
                <div className="wow-stat warn" style={{ marginBottom: 12 }}>
                  <div className="ws-label">Lock Status</div>
                  <div className="ws-value">
                    {timeLeft > 0 ? secondsToDHMS(timeLeft) + ' remaining' : 'Unlocked'}
                  </div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--grx-text-dim)', marginTop: 4 }}>
                    Unlocks: {tsToDate(lockEnd)}
                  </div>
                </div>
              )}

              <div className="wow-actions">
                {Number(inv.planType) === 0 && invType === 0 && !inv.principalWithdrawn && (
                  <NeonButton variant="danger" size="sm" onClick={() => withdraw(idx)} loading={busy}>
                    Withdraw Principal
                  </NeonButton>
                )}
                {hasLock && (
                  <a
                    className="neon-btn btn-ghost btn-sm"
                    style={{ textDecoration: 'none' }}
                    href={pinksaleLockUrl(inv.pinkSaleLockId, ACTIVE_NETWORK)}
                    target="_blank" rel="noreferrer"
                  >
                    🔒 View on PinkSale
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};


export default Invest;
